<template>
  <oare-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    title="Error Details"
    cancelText="Close"
    :showSubmit="false"
    :showCancel="false"
    :closeButton="true"
    :width="750"
    :persistent="false"
  >
    <v-container class="error-details">
      <v-row class="py-2">
        <div>
          <h3>Status</h3>
          <v-select
            :items="statusItems"
            :model-value="error.status"
            aria-label="Status"
            @update:model-value="updateStatus"
            class="test-status"
            density="compact"
          ></v-select>
        </div>
      </v-row>

      <v-row class="py-2">
        <div>
          <h3>User</h3>
          <span class="test-user-name">{{ error.userName || 'No User' }}</span>
        </div>
      </v-row>

      <v-row class="py-2">
        <div>
          <h3>Timestamp</h3>
          <span>{{ formatTimestamp(error.timestamp) }}</span>
        </div>
      </v-row>

      <v-row class="py-2">
        <div>
          <h3>Description</h3>
          <span class="test-description">{{ error.description }}</span>
        </div>
      </v-row>

      <v-row class="py-2" v-if="error.stacktrace">
        <div>
          <h3>Stacktrace</h3>
          <span class="test-stacktrace">{{ error.stacktrace }}</span>
        </div>
      </v-row>
    </v-container>
  </oare-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import OareDialog from '@/components/base/OareDialog.vue';
import { ErrorsRowWithUser, ErrorStatus } from '@oare/types';
import sl from '@/serviceLocator';
import { formatTimestamp, resetAdminBadge } from '@/utils';

export default defineComponent({
  name: 'ErrorLogDialog',
  components: {
    OareDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object as PropType<ErrorsRowWithUser>,
      required: true,
    },
  },
  emits: ['update:modelValue', 'update-status'],
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const statusItems: ErrorStatus[] = ['New', 'In Progress', 'Resolved'];

    const updateStatus = async (status: ErrorStatus | null) => {
      if (!status) {
        return;
      }
      try {
        emit('update-status', status);
        await server.updateErrorStatus([props.error.uuid], status);
        await resetAdminBadge();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error updating error status. Please try again.',
          err as Error,
          'updateErrorStatus failed'
        );
      }
    };

    return {
      formatTimestamp,
      updateStatus,
      statusItems,
    };
  },
});
</script>

<style scoped>
.error-details .v-row > div {
  width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}

.test-status {
  max-width: 200px;
}

.test-stacktrace {
  white-space: pre-wrap;
}
</style>
